import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Fade,
  Backdrop,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, DarkMode as DarkModeIcon, LightMode as LightModeIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import axios from 'axios';
import AuthSessionManager from './AuthSessionManager';
import { useTheme, AccentColorHoverWrapper } from './context/ThemeContext';
import API_BASE_URL from './config/api';
import { usePermissions } from './context/PermissionContext';

function LoginPage() {
  useEffect(() => { document.title = 'Sign In - Theezdocs'; }, []);
  const [forgotDone, setForgotDone] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [openSignUp, setOpenSignUp] = useState(false);
  const [signUpData, setSignUpData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verificationCode: '',
  });
  const [signUpStep, setSignUpStep] = useState(1);
  const [signUpError, setSignUpError] = useState('');
  const [signUpMessage, setSignUpMessage] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [selected2FAMethod, setSelected2FAMethod] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [userNo, setUserNo] = useState(null);
  const [signUpComplete, setSignUpComplete] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  // AWS End User Messaging DIGITAL_FORM opt-in: SMS consent + Terms are two
  // SEPARATE, unchecked checkboxes that must both be actively checked.
  const [smsConsent, setSmsConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { fetchPermissions } = usePermissions();

  const validatePassword = (pw) => {
    if (!pw || pw.length < 8) return 'Password must be at least 8 characters long.';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number.';
    return null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const authModel = { emailID: email, password };
      // withCredentials: true so the browser sends any existing `device_trust`
      // cookie. If the server finds it valid for this user it skips the 2FA
      // step and returns a full MFA-verified token directly (mfaRequired:false).
      const response = await axios.post(`${API_BASE_URL}/Login`, authModel, { withCredentials: true });
      console.log('Login Response:', response.data);

      if (response.data.success) {
        if (response.data.result) {
          localStorage.setItem('jwtToken', response.data.token);
          localStorage.setItem('userAccessLevel', response.data.accessLevel);
          localStorage.setItem('userEmail', email);
          // Store admin/owner status for permission system
          localStorage.setItem('isAdmin', response.data.isAdmin?.toString() || 'false');
          localStorage.setItem('isOwner', response.data.isOwner?.toString() || 'false');

          // Clear any stale per-user "2FA verified" flag from before the MFA
          // enforcement deploy. ProtectedRoute now keys off the JWT's `mfa`
          // claim, not this flag.
          const emailKey = (email || '').toLowerCase();
          if (emailKey) localStorage.removeItem(`is2FAVerified_${emailKey}`);

          localStorage.setItem('isLoggedIn', 'true');

          // Device-trust fast path: if the server honored the device_trust
          // cookie, it issued a full MFA-verified token and no OTP is needed.
          if (response.data.mfaRequired === false) {
            await fetchPermissions();
            navigate('/dashboard');
            return;
          }

          // Otherwise, route through the 2FA page as usual.
          localStorage.setItem('showQR', response.data.result.secretKey === '' ? 'true' : 'false');
          AuthSessionManager.save2FASession({
            qrCode: response.data.result.qrCodeImage2FA,
            userNo: response.data.result.userNo,
            secretKey: response.data.result.secretKey,
          });
          navigate('/2fa');
        } else {
          setError('Invalid credentials');
        }
      } else {
        setError(response.data.message || 'Invalid credentials');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please try again.';
      console.error('Login Error:', err.response?.data || err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('Sending... Please wait...');
    setForgotDone(false);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/ResetPassword/forgot-password`,
        { Email: forgotEmail }
      );

      setForgotMessage(response.data.message);

      if (response.data.success) {
        setForgotEmail('');
        setForgotDone(true);
      }
    } catch (err) {
      setForgotMessage(err.response?.data?.message || 'An error occurred. Please try again.');
    }
  };

  const handleSignUpInputChange = (e) => {
    const { name, value } = e.target;
    setSignUpData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setSignUpError('');
    setSignUpLoading(true);

    if (signUpStep === 1) {
      const allowedDomains = ['safavieh.com', 'seidal.com', 'top-line.com'];
      const emailLower = signUpData.email.toLowerCase().trim();
      const atIndex = emailLower.lastIndexOf('@');
      const domain = atIndex >= 1 ? emailLower.substring(atIndex + 1) : '';
      if (!allowedDomains.includes(domain)) {
        setSignUpError('Only @safavieh.com, @seidal.com, and @top-line.com email domains are allowed.');
        setSignUpLoading(false);
        return;
      }

      const pwErr = validatePassword(signUpData.password);
      if (pwErr) {
        setSignUpError(pwErr);
        setSignUpLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/SignUp`, {
          firstName: signUpData.firstName,
          lastName: signUpData.lastName,
          emailID: signUpData.email,
          password: signUpData.password,
        });

        if (response.data.success) {
          // Note: userNo is returned by VerifyEmailCode, not SignUp
          setSignUpMessage('Verification code sent to your email.');
          setSignUpStep(2);
        } else {
          setSignUpError(response.data.message || 'Failed to send verification code.');
        }
      } catch (err) {
        setSignUpError(err.response?.data?.message || 'An error occurred. Please try again.');
      } finally {
        setSignUpLoading(false);
      }
    } else if (signUpStep === 2) {
      try {
        const response = await axios.post(`${API_BASE_URL}/VerifyEmailCode`, {
          emailID: signUpData.email,
          code: signUpData.verificationCode,
        });

        if (response.data.success) {
          setUserNo(response.data.userNo);
          setQrCode(response.data.qrCode);
          setSecretKey(response.data.secretKey);
          // Store JWT token returned after email verification
          localStorage.setItem('jwtToken', response.data.token);
          localStorage.setItem('userAccessLevel', response.data.accessLevel);
          setSignUpMessage('Email verified successfully! Set up 2FA (optional) or proceed to login.');
          setSignUpStep(3);
        } else {
          setSignUpError(response.data.message || 'Email verification failed.');
        }
      } catch (err) {
        setSignUpError(err.response?.data?.message || 'An error occurred during verification.');
      } finally {
        setSignUpLoading(false);
      }
    }
  };

  const handle2FASetup = async (e, method) => {
    e.preventDefault();
    setSignUpError('');

    // For phone method, show phone input first if not already provided
    if (method === 'phone' && !phoneNumber) {
      setShowPhoneInput(true);
      return;
    }

    setSignUpLoading(true);

    if (method !== 'authApp') {
      // Build request body with userNo and phoneNumber (for SMS)
      const requestBody = { userNo: userNo };
      if (method === 'phone') {
        // Format phone number - ensure it has country code
        let formattedPhone = phoneNumber.trim();
        if (!formattedPhone.startsWith('+')) {
          // Default to US country code if not provided
          formattedPhone = '+1' + formattedPhone.replace(/\D/g, '');
        }
        requestBody.phoneNumber = formattedPhone;
        console.log('Sending SMS request with phone:', formattedPhone, 'userNo:', userNo);
      }

      try {
        console.log('Request body:', JSON.stringify(requestBody));
        const response = await axios.post(
          `${API_BASE_URL}/send${method === 'phone' ? 'Sms' : 'Email'}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
            },
          }
        );

        if (response.data.success) {
          setSelected2FAMethod(method);
          setShowPhoneInput(false);
        } else {
          setSignUpError(response.data.message || 'Failed to send 2FA code.');
          setSignUpLoading(false);
        }
      } catch (err) {
        console.error('2FA setup error:', err.response?.data || err);
        setSignUpError(err.response?.data?.message || 'An error occurred.');
        setSignUpLoading(false);
      }
    } else {
      setSelected2FAMethod(method);
      setSignUpLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setSignUpError('');
    setSignUpLoading(true);

    try {
      const verifyOTPModel = {
        userNo: userNo,
        otp: twoFACode,
        secretKey: selected2FAMethod === 'authApp' ? secretKey : undefined,
        method: selected2FAMethod,
      };

      const response = await axios.post(`${API_BASE_URL}/verifyOTP`, verifyOTPModel, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
        },
      });

      if (response.data.success) {
        // Upgrade the stored pre-MFA token to the new full token (if present).
        // Signup finishes by asking the user to log in again, so this is mostly
        // cosmetic — but keeps the stored token consistent in case anything
        // tries to use it before the re-login.
        if (response.data.token) {
          localStorage.setItem('jwtToken', response.data.token);
        }
        setSignUpMessage('2FA setup complete! You can now log in.');
        setSignUpComplete(true);
        setTimeout(() => {
          setOpenSignUp(false);
          setSignUpStep(1);
          setSignUpData({ firstName: '', lastName: '', email: '', password: '', verificationCode: '' });
          setSelected2FAMethod('');
          setTwoFACode('');
          setSignUpComplete(false);
        }, 2000);
      } else {
        setSignUpError(response.data.message || 'Invalid 2FA code.');
      }
    } catch (err) {
      setSignUpError(err.response?.data?.message || '2FA verification failed.');
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleSkip2FA = () => {
    setSignUpMessage('Sign-up complete! You can now log in.');
    setSignUpComplete(true);
  };

  const logoSrc = isDarkMode
    ? '/safavieh-icon-dark.png'
    : '/safavieh-icon-light.png';

  const bgLight = 'https://safaviehuploads.s3.amazonaws.com/login-backgrounds/login-bg-light.png';
  const bgDark = 'https://safaviehuploads.s3.amazonaws.com/login-backgrounds/login-bg-dark.png';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${isDarkMode ? bgDark : bgLight})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: isDarkMode ? '#131415' : '#ffffff',
        transition: 'background-color 0.3s ease',
        position: 'relative',
      }}
    >
      {/* Theme toggle (with hover-popover accent picker) — top-right of the
          login viewport. Floats over the background image / panel so users
          can flip dark/light + change accent before they sign in. */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1300 }}>
        <AccentColorHoverWrapper>
          <Tooltip title="Toggle Theme">
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: isDarkMode ? '#e8ecef' : '#1b2a41',
                bgcolor: isDarkMode ? 'rgba(19,20,21,0.6)' : 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
              }}
            >
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </AccentColorHoverWrapper>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '400px',
          p: { xs: 2, sm: 3 },
          backgroundColor: isDarkMode ? 'rgba(19,20,21,0.85)' : 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          border: isDarkMode ? '1px solid rgba(187,176,255,0.1)' : '1px solid rgba(27,42,65,0.08)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0,0,0,0.5)'
            : '0 8px 32px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ mb: { xs: 1.5, sm: 2 }, mt: { xs: 0.5, sm: 1 }, width: { xs: 90, sm: 120 }, height: { xs: 90, sm: 120 }, pointerEvents: 'none', userSelect: 'none' }}>
          <img
            src={logoSrc}
            alt="Logo"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>

        <Typography
          variant="h4"
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 1,
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            whiteSpace: 'nowrap',
          }}
        >
          Welcome to Safavieh
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
          Please sign in to access your account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, bgcolor: 'error.main', color: 'background.paper' }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin} style={{ width: '100%' }}>
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                '& fieldset': { borderColor: 'secondary.main' },
                '&:hover fieldset': { borderColor: 'primary.main' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
              '& .MuiInputLabel-root': { color: 'text.secondary' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
            }}
            InputProps={{ style: { color: 'text.primary' } }}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'background.paper',
                '& fieldset': { borderColor: 'secondary.main' },
                '&:hover fieldset': { borderColor: 'primary.main' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
              },
              '& .MuiInputLabel-root': { color: 'text.secondary' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
              '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
            }}
            InputProps={{
              style: { color: 'text.primary' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                    {showPassword ? (
                      <VisibilityOff sx={{ color: 'primary.main' }} />
                    ) : (
                      <Visibility sx={{ color: 'primary.main' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              color: 'background.paper',
              '&:hover': { backgroundColor: 'primary.dark' },
              '&:disabled': { backgroundColor: 'primary.main', opacity: 0.5, color: 'background.paper' },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'background.paper' }} /> : 'Login'}
          </Button>
        </form>

        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}>
          <Box
            component="a"
            href="#"
            onClick={() => setOpenForgotPassword(true)}
            sx={{
              color: isDarkMode ? '#90caf9' : '#1976d2',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            I lost my password
          </Box>
          {' | '}
          <Box
            component="a"
            href="#"
            onClick={() => setOpenSignUp(true)}
            sx={{
              color: isDarkMode ? '#90caf9' : '#1976d2',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Sign Up
          </Box>
        </Typography>

        {/* Forgot Password Modal */}
        <Modal
          open={openForgotPassword}
          onClose={() => {
            setOpenForgotPassword(false);
            setForgotDone(false);
            setForgotMessage('');
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Fade in={openForgotPassword}>
            <Box
              sx={{
                position: 'absolute',
                top: '55%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: '400px',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                Reset Password
              </Typography>

              {forgotMessage && (
                <Alert
                  severity={
                    forgotMessage.toLowerCase().includes('successfully')
                      ? 'success'
                      : forgotMessage.toLowerCase().includes('sending')
                        ? 'info'
                        : 'error'
                  }
                  sx={{ mb: 2 }}
                >
                  {forgotMessage}
                </Alert>
              )}

              {forgotDone ? (
                <Button
                  onClick={() => {
                    setOpenForgotPassword(false);
                    setForgotDone(false);
                    setForgotMessage('');
                  }}
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: '8px',
                    backgroundColor: 'primary.main',
                    color: 'background.paper',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }}
                >
                  Close
                </Button>
              ) : (
                <form onSubmit={handleForgotPassword}>
                  <TextField
                    label="Email"
                    type="email"
                    variant="outlined"
                    fullWidth
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'background.paper',
                        '& fieldset': { borderColor: 'secondary.main' },
                      },
                      '& .MuiInputLabel-root': { color: 'text.secondary' },
                    }}
                    InputProps={{ style: { color: 'text.primary' } }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: '8px',
                      backgroundColor: 'primary.main',
                      color: 'background.paper',
                      '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                  >
                    Send Reset Link
                  </Button>

                  <Button
                    onClick={() => {
                      setOpenForgotPassword(false);
                      setForgotDone(false);
                      setForgotMessage('');
                    }}
                    fullWidth
                    variant="outlined"
                    sx={{
                      mt: 1,
                      py: 1.5,
                      borderRadius: '8px',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
                    }}
                  >
                    Cancel
                  </Button>
                </form>
              )}
            </Box>
          </Fade>
        </Modal>

        {/* Sign-Up Modal */}
        <Modal
          open={openSignUp}
          onClose={() => setOpenSignUp(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Fade in={openSignUp}>
            <Box
              sx={{
                position: 'absolute',
                top: '55%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: '400px',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                {signUpStep === 1 ? 'Sign Up' : signUpStep === 2 ? 'Verify Email' : 'Set Up 2FA (Optional)'}
              </Typography>
              {signUpMessage && (
                <Alert severity={signUpMessage.includes('success') || signUpMessage.includes('Sign-up complete') ? 'success' : 'info'} sx={{ mb: 2 }}>
                  {signUpMessage}
                </Alert>
              )}
              {signUpError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {signUpError}
                </Alert>
              )}

              {signUpComplete ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    onClick={() => setOpenSignUp(false)}
                    variant="outlined"
                    sx={{
                      py: 1.5,
                      borderRadius: '8px',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              ) : signUpStep <= 2 ? (
                <form onSubmit={handleSignUpSubmit}>
                  {signUpStep === 1 && (
                    <>
                      <TextField
                        label="First Name"
                        name="firstName"
                        variant="outlined"
                        fullWidth
                        required
                        value={signUpData.firstName}
                        onChange={handleSignUpInputChange}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />
                      <TextField
                        label="Last Name"
                        name="lastName"
                        variant="outlined"
                        fullWidth
                        required
                        value={signUpData.lastName}
                        onChange={handleSignUpInputChange}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />
                      <TextField
                        label="Email (@safavieh.com, @seidal.com, @top-line.com)"
                        name="email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        required
                        value={signUpData.email}
                        onChange={handleSignUpInputChange}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />
                      <TextField
                        label="Password"
                        name="password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        required
                        value={signUpData.password}
                        onChange={handleSignUpInputChange}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />
                    </>
                  )}
                  {signUpStep === 2 && (
                    <TextField
                      label="Verification Code"
                      name="verificationCode"
                      variant="outlined"
                      fullWidth
                      required
                      value={signUpData.verificationCode}
                      onChange={handleSignUpInputChange}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'background.paper',
                          '& fieldset': { borderColor: 'secondary.main' },
                        },
                        '& .MuiInputLabel-root': { color: 'text.secondary' },
                      }}
                      InputProps={{ style: { color: 'text.primary' } }}
                    />
                  )}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={signUpLoading}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: '8px',
                      backgroundColor: 'primary.main',
                      color: 'background.paper',
                      '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                  >
                    {signUpLoading ? <CircularProgress size={24} /> : signUpStep === 1 ? 'Send Verification Code' : 'Verify Code'}
                  </Button>
                  <Button
                    onClick={() => setOpenSignUp(false)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      mt: 1,
                      py: 1.5,
                      borderRadius: '8px',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
                    }}
                  >
                    Cancel
                  </Button>
                </form>
              ) : (
                <>
                  {!selected2FAMethod && !showPhoneInput ? (
                    <Box width="100%" display="flex" flexDirection="column" gap={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => handle2FASetup(e, 'authApp')}
                        sx={{
                          py: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'primary.main',
                          color: 'background.paper',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }}
                      >
                        Set Up with Authenticator App
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled
                        sx={{
                          py: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'grey.400',
                          color: 'background.paper',
                          '&:hover': { backgroundColor: 'grey.400' },
                          '&.Mui-disabled': { backgroundColor: 'grey.400', color: 'grey.600' },
                        }}
                      >
                        Set Up with Phone (Coming Soon)
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleSkip2FA}
                        sx={{
                          mt: 1,
                          py: 1.5,
                          borderRadius: '8px',
                          color: 'primary.main',
                          borderColor: 'primary.main',
                          '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
                        }}
                      >
                        Skip 2FA Setup
                      </Button>
                    </Box>
                  ) : showPhoneInput && !selected2FAMethod ? (
                    <Box width="100%" display="flex" flexDirection="column" gap={2}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Enter your phone number to receive a verification code via SMS
                      </Typography>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        required
                        placeholder="+1234567890"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />

                      {/* SMS consent — separate, unchecked checkbox with all
                          required disclosures (AWS DIGITAL_FORM opt-in). */}
                      <FormControlLabel
                        sx={{ alignItems: 'flex-start', m: 0 }}
                        control={
                          <Checkbox
                            checked={smsConsent}
                            onChange={(e) => setSmsConsent(e.target.checked)}
                            size="small"
                            sx={{ pt: 0 }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                            By checking, you consent to receive <strong>one-time passcodes and verification codes</strong> from
                            Safavieh. Message frequency may vary. Message and data rates may apply. Reply HELP for help or STOP to opt-out.
                          </Typography>
                        }
                      />

                      {/* Terms/Privacy — a SEPARATE checkbox, never bundled with SMS consent. */}
                      <FormControlLabel
                        sx={{ alignItems: 'flex-start', m: 0 }}
                        control={
                          <Checkbox
                            checked={termsConsent}
                            onChange={(e) => setTermsConsent(e.target.checked)}
                            size="small"
                            sx={{ pt: 0 }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                            By checking, I accept the{' '}
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>Terms of Service</Box>
                            {' '}&amp;{' '}
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>Privacy Policy</Box>.
                          </Typography>
                        }
                      />

                      <Button
                        variant="contained"
                        fullWidth
                        disabled={signUpLoading || !phoneNumber || !smsConsent || !termsConsent}
                        onClick={(e) => handle2FASetup(e, 'phone')}
                        sx={{
                          py: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'primary.main',
                          color: 'background.paper',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }}
                      >
                        {signUpLoading ? <CircularProgress size={24} /> : 'Send Code'}
                      </Button>
                      <Button
                        variant="text"
                        fullWidth
                        onClick={() => {
                          setShowPhoneInput(false);
                          setPhoneNumber('');
                          setSignUpError('');
                          setSmsConsent(false);
                          setTermsConsent(false);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Back to method selection
                      </Button>
                    </Box>
                  ) : (
                    <form onSubmit={handle2FAVerify}>
                      {selected2FAMethod === 'authApp' && qrCode && (
                        <Box display="flex" justifyContent="center" my={2}>
                          <img
                            src={`data:image/png;base64,${qrCode}`}
                            alt="2FA QR Code"
                            style={{
                              maxWidth: '300px',
                              maxHeight: '300px',
                              width: '100%',
                              height: 'auto',
                              border: '1px solid',
                              borderColor: '#1976d2',
                            }}
                          />
                        </Box>
                      )}
                      <TextField
                        label={selected2FAMethod === 'authApp' ? 'Enter code from app' : selected2FAMethod === 'phone' ? 'Enter phone verification code' : 'Enter email verification code'}
                        fullWidth
                        required
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value)}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'background.paper',
                            '& fieldset': { borderColor: 'secondary.main' },
                          },
                          '& .MuiInputLabel-root': { color: 'text.secondary' },
                        }}
                        InputProps={{ style: { color: 'text.primary' } }}
                      />
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={signUpLoading}
                        sx={{
                          py: 1.5,
                          borderRadius: '8px',
                          backgroundColor: 'primary.main',
                          color: 'background.paper',
                          '&:hover': { backgroundColor: 'primary.dark' },
                        }}
                      >
                        {signUpLoading ? <CircularProgress size={24} /> : 'Verify 2FA'}
                      </Button>
                      <Button
                        onClick={() => setSelected2FAMethod('')}
                        fullWidth
                        variant="outlined"
                        sx={{
                          mt: 1,
                          py: 1.5,
                          borderRadius: '8px',
                          color: 'primary.main',
                          borderColor: 'primary.main',
                          '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' },
                        }}
                      >
                        Choose a Different Method
                      </Button>
                    </form>
                  )}
                </>
              )}
            </Box>
          </Fade>
        </Modal>
      </Box>
    </Box>
  );
}

export default LoginPage;